// src/Coaching/coachingCategories.ts
export const coachingCategories = [
  {
    slug: "selective-oc",
    accent: "teal",
    name: "Selective Entry & Opportunity Class",

    description:
      "Focused guided preparation for competitive government-school entry examinations across Victoria and NSW.",

    longDescription: `
# 🧠 Selective & Opportunity Class Preparation

## 🎯 Competitive School Entry

Selective Entry and Opportunity Class examinations are competitive pathways into high-performing government schools across Victoria and NSW. They require more than familiarity with schoolwork and place significant emphasis on reasoning, problem-solving, accuracy and performance under time pressure.

- 🧠 **Reasoning** — logical and abstract thinking
- 🔢 **Mathematics** — problem-solving and numerical reasoning
- 📖 **Reading** — comprehension, interpretation and analysis
- ✍️ **Writing** — clear thinking and effective expression

---

## 👨‍🏫 Guided Preparation with Eduvic

Guided preparation combines scheduled online sessions with structured practice and assessment through the Eduvic Practice Platform and Learning Management System.

- 🧑‍🏫 **Scheduled Online Sessions** — work with an Eduvic mentor through structured online classes focused on the requirements of your examination
- 📝 **Practice Platform Access** — receive access to relevant Eduvic Practice content for continued work between sessions
- 📊 **Periodic Assessments** — complete scheduled assessments and examinations through the Eduvic Learning Management System
- 📈 **Performance Review** — use practice and assessment results to identify areas requiring further attention
- 📚 **Digital Learning Material** — access relevant lessons and supporting material through the LMS as preparation progresses

The objective is to combine **guided instruction, independent practice and regular assessment** so that preparation can be adjusted as your performance develops.

---

### 📚 Explore Your Exam Pathway

Choose the relevant exam pathway below for detailed information about the examination, preparation approach, and the guided preparation and Eduvic resources available for that pathway.
`,

    domains: [
      {
        slug: "vic-year9",
        name: "Victoria Selective Entry",
        subtitle: "Target Year 9",

        description: `
🎯 **Victorian Selective Entry — Year 9**

🏫 **Schools**
- Melbourne High School
- Mac.Robertson Girls' High School
- Nossal High School
- Suzanne Cory High School

📚 **Preparation Focus**
- 🧠 Reasoning and problem solving
- 🔢 Mathematical reasoning
- 📖 Reading and comprehension
- ✍️ Written expression
- ⏱️ Accuracy and time management
`,

        accent: "teal",
      },

      {
        slug: "vic-year10",
        name: "Victoria Selective Entry",
        subtitle: "Target Year 10",

        description: `
🎯 **Victorian Selective Entry — Year 10**

🏫 **Selective Schools**
- Melbourne High School
- Mac.Robertson Girls' High School
- Nossal High School
- Suzanne Cory High School

📚 **Preparation Focus**
- 🧠 Mathematical and verbal reasoning
- 🔢 Quantitative problem solving
- 📖 Reading comprehension
- ✍️ Writing
- ⏱️ Examination technique and time management
`,

        accent: "teal",
      },

      {
        slug: "nsw-year7",
        name: "NSW Selective Entry",
        subtitle: "Target Year 7",

        description: `
🎯 **NSW Selective High School — Year 7**

📋 **Placement Test**
Preparation for the NSW Selective High School Placement Test and its competitive academic entry requirements.

📚 **Preparation Focus**
- 🧠 Thinking and reasoning
- 🔢 Mathematical reasoning
- 📖 Reading
- ✍️ Writing
- ⏱️ Accuracy and time management
`,

        accent: "cyan",
      },

      {
        slug: "nsw-oc-year5",
        name: "NSW Opportunity Class",
        subtitle: "Target Year 5",

        description: `
🎯 **NSW Opportunity Class — Year 5**

📋 **Placement Test**
Preparation for the NSW Opportunity Class Placement Test and the academic reasoning required for competitive placement.

📚 **Preparation Focus**
- 🧠 Thinking and reasoning
- 🔢 Mathematical reasoning
- 📖 Reading
- ✍️ Writing
- ⏱️ Accuracy and time management
`,

        accent: "cyan",
      },
    ],
  },

  {
    slug: "scholarship",
    accent: "purple",
    name: "Scholarship & Entrance",

    description:
      "Guided preparation for scholarship and competitive entrance examinations conducted by independent testing organisations.",

    longDescription: `
# 🎓 Scholarship & Entrance Preparation

## 🎯 Competitive Scholarship & School Entry

Scholarship and competitive entrance examinations are used by independent schools to assess academic ability and identify students for scholarship or selective entry opportunities. The assessment can extend beyond curriculum knowledge to include reasoning, comprehension, problem-solving and written communication.

- 🧠 **Reasoning** — logical, verbal and numerical thinking
- 🔢 **Mathematics** — numerical understanding and problem-solving
- 📖 **Reading** — comprehension, interpretation and analysis
- ✍️ **Writing** — communication, structure and effective expression

---

## 👨‍🏫 Guided Preparation with Eduvic

Guided preparation combines scheduled online sessions with structured practice and assessment through the Eduvic Practice Platform and Learning Management System, with preparation aligned to the requirements of the selected scholarship or entrance examination.

- 🧑‍🏫 **Scheduled Online Sessions** — work with an Eduvic mentor through structured online classes focused on the relevant examination
- 📝 **Practice Platform Access** — receive access to relevant Eduvic Practice content for continued preparation between sessions
- 📊 **Periodic Assessments** — complete scheduled assessments and examinations through the Eduvic Learning Management System
- 📈 **Performance Review** — use practice and assessment results to identify strengths and areas requiring further work
- 📚 **Digital Learning Material** — access relevant lessons and supporting material through the LMS as preparation progresses

The objective is to combine **guided instruction, independent practice and regular assessment** so that preparation can be adjusted as your performance develops.

---

### 📚 Explore Your Exam Pathway

Choose the relevant testing pathway below for detailed information about the examination, preparation approach, and the guided preparation and Eduvic resources available for that pathway.
`,

    domains: [
      {
        slug: "edutest",
        name: "Scholarship & Entrance",
        subtitle: "EduTest",

        description: `
🎯 **EduTest Scholarship & Entrance**

📋 **Assessment Areas**
EduTest examinations can assess a combination of academic ability, reasoning and written communication depending on the school and scholarship program.

📚 **Preparation Focus**
- 🧠 Verbal reasoning
- 🔢 Numerical reasoning
- ➗ Mathematics
- 📖 Reading and comprehension
- ✍️ Written expression
`,

        accent: "purple",
      },

      {
        slug: "acer",
        name: "Scholarship & Entrance",
        subtitle: "ACER",

        description: `
🎯 **ACER Scholarship & Entrance**

📋 **Assessment Areas**
ACER-based scholarship examinations assess academic and reasoning ability, with the exact assessment structure determined by the relevant scholarship or entrance program.

📚 **Preparation Focus**
- 📖 Reading
- 🔢 Mathematical reasoning
- 🧠 Problem solving
- ✍️ Written expression
- ⏱️ Examination technique
`,

        accent: "pink",
      },
    ],
  },
];;